<!-- src/components/forms/completed/MultiItemInspectionCompleted.vue -->
<template>
  <div class="space-y-12 text-left animate-fadeIn">
    
    <!-- 1. SITE & SHIFT DETAILS -->
    <div class="bg-slate-50 p-8 rounded-[32px] border border-slate-200 shadow-inner">
      <h3 class="text-xs font-black text-[#0A1630] uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
        <ShieldCheckIcon class="h-4 w-4 text-[#20FCF1]" /> 1. Site & Shift Details
      </h3>
      <div class="grid grid-cols-2 md:grid-cols-4 gap-6 text-left">
        <div>
          <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest">Site / Location</p>
          <p class="text-xs font-black text-[#0A1630] uppercase mt-1">{{ submissionData.siteDetails?.siteLocation || 'N/A' }}</p>
        </div>
        <div>
          <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest">Date</p>
          <p class="text-xs font-bold text-slate-700 uppercase mt-1">{{ submissionData.siteDetails?.date || 'N/A' }}</p>
        </div>
        <div>
          <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest">Shift / Time</p>
          <p class="text-xs font-bold text-slate-700 uppercase mt-1">{{ submissionData.siteDetails?.shiftTime || 'N/A' }}</p>
        </div>
        <div>
          <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest">Inspected By</p>
          <p class="text-xs font-bold text-slate-700 uppercase mt-1">{{ submissionData.siteDetails?.inspectedBy || 'N/A' }}</p>
        </div>
      </div>
    </div>

    <!-- 2. ITEMS INSPECTED TABLE -->
    <div class="space-y-4">
      <h3 class="text-xs font-black text-[#0A1630] uppercase tracking-[0.2em] ml-1">2. Items Inspected (Shift Check)</h3>
      <div class="overflow-x-auto rounded-[24px] border border-slate-200 bg-white shadow-sm">
        <table class="min-w-full divide-y divide-slate-200 text-left">
          <thead class="bg-[#0A1630] text-white">
            <tr>
              <th class="px-5 py-3 text-[9px] font-black text-[#20FCF1] uppercase tracking-widest">#</th>
              <th class="px-5 py-3 text-[9px] font-black text-[#20FCF1] uppercase tracking-widest">Asset ID / Desc</th>
              <th class="px-5 py-3 text-[9px] font-black text-[#20FCF1] uppercase tracking-widest">Serial No.</th>
              <th class="px-3 py-3 text-[9px] font-black text-center uppercase tracking-widest">Visual</th>
              <th class="px-3 py-3 text-[9px] font-black text-center uppercase tracking-widest">Guards</th>
              <th class="px-3 py-3 text-[9px] font-black text-center uppercase tracking-widest">Cables</th>
              <th class="px-3 py-3 text-[9px] font-black text-center uppercase tracking-widest">Function</th>
              <th class="px-5 py-3 text-[9px] font-black text-[#20FCF1] uppercase tracking-widest">Result</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100">
            <template v-for="(item, idx) in (submissionData.items || [])" :key="idx">
              <tr v-if="item.assetId" class="hover:bg-slate-50">
                <td class="px-5 py-3 text-xs font-black text-slate-400">{{ idx + 1 }}</td>
                <td class="px-5 py-3 text-xs font-black text-[#0A1630] uppercase">{{ item.assetId }}</td>
                <td class="px-5 py-3 text-xs font-bold text-slate-600 uppercase">{{ item.serialNo || '—' }}</td>
                <td class="px-3 py-3 text-center text-xs font-bold">{{ item.visualOk }}</td>
                <td class="px-3 py-3 text-center text-xs font-bold">{{ item.guardsOk }}</td>
                <td class="px-3 py-3 text-center text-xs font-bold">{{ item.cablesOk }}</td>
                <td class="px-3 py-3 text-center text-xs font-bold">{{ item.functionOk }}</td>
                <td class="px-5 py-3">
                  <span class="px-3 py-1 text-[9px] font-black rounded-full uppercase" :class="item.result === 'PASS' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'">
                    {{ item.result }}
                  </span>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 3. DEFECTS -->
    <div v-if="submissionData.defectsDetails || submissionData.defectEvidenceUrls?.length" class="bg-rose-50/50 p-6 rounded-[28px] border border-rose-100 space-y-4">
      <h3 class="text-xs font-black text-rose-800 uppercase tracking-[0.2em]">3. Defects Found / Actions Taken</h3>
      <p class="text-xs font-medium text-slate-700 italic">"{{ submissionData.defectsDetails || 'No defects recorded.' }}"</p>
      
      <div v-if="submissionData.defectEvidenceUrls?.length" class="flex flex-wrap gap-4 pt-2">
        <a v-for="(url, idx) in submissionData.defectEvidenceUrls" :key="idx" :href="url" target="_blank" class="block h-20 w-20 rounded-xl overflow-hidden border shadow-sm hover:scale-105 transition-all">
          <img :src="url" class="w-full h-full object-cover" alt="Defect Evidence" />
        </a>
      </div>
    </div>

    <!-- 4. SIGN-OFF -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-slate-100">
      <div class="bg-slate-50 p-6 rounded-3xl border border-slate-200">
        <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Inspector Sign-Off</p>
        <p class="text-sm font-black text-[#0A1630] uppercase">{{ submissionData.signOff?.inspectorName || 'Inspector' }}</p>
        <div v-if="submissionData.signOff?.inspectorSig" class="mt-4 bg-white p-3 rounded-2xl border inline-block">
          <img :src="submissionData.signOff.inspectorSig" class="h-12 object-contain grayscale" alt="Inspector Signature" />
        </div>
      </div>
      <div v-if="submissionData.signOff?.supervisorName" class="bg-slate-50 p-6 rounded-3xl border border-slate-200">
        <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Supervisor Sign-Off</p>
        <p class="text-sm font-black text-[#0A1630] uppercase">{{ submissionData.signOff.supervisorName }}</p>
        <div v-if="submissionData.signOff?.supervisorSig" class="mt-4 bg-white p-3 rounded-2xl border inline-block">
          <img :src="submissionData.signOff.supervisorSig" class="h-12 object-contain grayscale" alt="Supervisor Signature" />
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ShieldCheckIcon } from '@heroicons/vue/24/outline';
defineProps({
  submissionData: { type: Object, required: true }
});
</script>